import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Receipt,
  Search,
  Calendar,
  Filter,
  CreditCard,
  CheckCircle2,
  XCircle,
  FileText
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import InputField from '../components/common/InputField';
import SelectField from '../components/common/SelectField';
import Button from '../components/common/Button';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import { fetchData } from '../api/api';
import { formatCurrency } from '../utils/currency';
import { formatDateTime } from '../utils/date';
import useDebounce from '../utils/debounce';

const SalesPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalSales, setTotalSales] = useState(0);

  const debouncedStartDate = useDebounce(startDate, 600);
  const debouncedEndDate = useDebounce(endDate, 600);
  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, debouncedStartDate, debouncedEndDate]);

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(sevenDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchSales = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (debouncedStartDate) params.append('startDate', debouncedStartDate);
      if (debouncedEndDate) params.append('endDate', debouncedEndDate);
      params.append('pageSize', pageSize.toString());
      params.append('pageNumber', currentPage.toString());

      const endpoint = `api/pos/v1/order/search?${params.toString()}`;
      const data = await fetchData(endpoint, 'GET', null, authToken, selectedShopCode);

      setSales(data.data?.data || []);
      setTotalPages(data.data?.last_page || 1);
      setTotalItems(data.data?.total || 0);
      setTotalSales(data.data?.total_sales || 0);
    } catch (err) {
      setError('Failed to fetch sales records.');
      setSales([]);
      setTotalPages(1);
      setTotalItems(0);
      setTotalSales(0);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, debouncedStartDate, debouncedEndDate, debouncedSearchTerm, currentPage, pageSize]);

  useEffect(() => {
    if (debouncedStartDate && debouncedEndDate) {
      fetchSales();
    }
  }, [fetchSales, debouncedStartDate, debouncedEndDate, pageSize, currentPage]);

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setIsDetailModalOpen(true);
  };

  const tableHeaders = ['Order Reference', 'Date & Time', 'Gross Amount', 'Net Amount', 'Status', 'Action'];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Orders & Invoices"
        subtitle="Track completed transactions, order totals, and customer receipts"
        breadcrumbs={[{ label: 'Sales Orders' }]}
      />

      {/* Date Filter & Search Controls */}
      <GlassmorphicCard noPadding>
        <div className="p-4 md:p-5 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            <div className="w-full sm:w-72">
              <InputField
                placeholder="Search order number or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-44">
              <InputField
                type="date"
                label=""
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                icon={<Calendar className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-44">
              <InputField
                type="date"
                label=""
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                icon={<Calendar className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-32">
              <SelectField
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                options={[
                  { label: '10 / page', value: 10 },
                  { label: '25 / page', value: 25 },
                  { label: '50 / page', value: 50 },
                  { label: '100 / page', value: 100 },
                ]}
                className="!mb-0"
              />
            </div>
            <Button onClick={fetchSales} icon={<Filter className="w-3.5 h-3.5" />} size="sm">
              Apply
            </Button>
          </div>
        </div>
      </GlassmorphicCard>

      {/* Total Sales Summary Banner */}
      {!loading && sales.length > 0 && (
        <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-emerald-100 font-semibold uppercase tracking-wider">
                Total Period Sales Volume ({totalItems} Orders)
              </p>
              <h3 className="text-2xl font-bold text-white tracking-tight mt-0.5">
                {formatCurrency(totalSales)}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Sales Orders Table */}
      <Table
        headers={tableHeaders}
        data={sales}
        loading={loading}
        emptyMessage={error || 'No sales orders found for this period.'}
        renderRow={(sale) => {
          const isPaid = sale.status === 'PAID';
          const isCanceled = sale.status === 'CANCELED';

          return (
            <tr
              key={sale.id}
              className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 shrink-0">
                    <Receipt className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 dark:text-white text-sm block">
                      {sale.orderNumber}
                    </span>
                    {sale.note && (
                      <span className="text-[11px] text-slate-400 truncate max-w-[160px] block">
                        {sale.note}
                      </span>
                    )}
                  </div>
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                {formatDateTime(sale.createdAt)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 line-through">
                {formatCurrency(sale.grossAmount)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(sale.netAmount)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    isPaid
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                      : isCanceled
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  {isPaid ? <CheckCircle2 className="w-3 h-3" /> : isCanceled ? <XCircle className="w-3 h-3" /> : null}
                  {sale.status}
                </span>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-right">
                <Button
                  onClick={() => handleViewDetails(sale)}
                  variant="light"
                  size="sm"
                  icon={<FileText className="w-3.5 h-3.5" />}
                >
                  Invoice
                </Button>
              </td>
            </tr>
          );
        }}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing <span className="font-semibold text-slate-900 dark:text-white">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{Math.min(currentPage * pageSize, totalItems)}</span> of{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{totalItems}</span> orders
          </p>

          <div className="flex items-center gap-1.5">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1}
              variant="light"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              variant="light"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={`Invoice • ${selectedSale?.orderNumber || ''}`}
        size="large"
      >
        {selectedSale && (
          <div className="space-y-5 text-xs">
            {/* Header Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
              <div>
                <p className="text-slate-400">Order Number</p>
                <p className="font-bold text-slate-900 dark:text-white text-sm">{selectedSale.orderNumber}</p>
              </div>
              <div>
                <p className="text-slate-400">Status</p>
                <span className="px-2 py-0.5 rounded-full font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300">
                  {selectedSale.status}
                </span>
              </div>
              <div>
                <p className="text-slate-400">Date & Time</p>
                <p className="font-medium text-slate-700 dark:text-slate-200">{formatDateTime(selectedSale.createdAt)}</p>
              </div>
              <div>
                <p className="text-slate-400">Total Net Amount</p>
                <p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm">{formatCurrency(selectedSale.netAmount)}</p>
              </div>
            </div>

            {/* Line Items Table */}
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Purchased Items</h4>
              {(() => {
                let orderItems = selectedSale.orderItems;
                if (typeof orderItems === 'string') {
                  try { orderItems = JSON.parse(orderItems); } catch (e) { orderItems = []; }
                }

                return Array.isArray(orderItems) && orderItems.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-700/80">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-semibold">Item</th>
                          <th className="px-4 py-2.5 text-center font-semibold">Quantity</th>
                          <th className="px-4 py-2.5 text-right font-semibold">Unit Price</th>
                          <th className="px-4 py-2.5 text-right font-semibold">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {orderItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30">
                            <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">
                              {item.productName}
                            </td>
                            <td className="px-4 py-2.5 text-center font-bold text-slate-700 dark:text-slate-300">
                              {item.quantity}
                            </td>
                            <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-400">
                              {formatCurrency(item.sellingPrice)}
                            </td>
                            <td className="px-4 py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(item.quantity * item.sellingPrice)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-400">No items found for this order.</p>
                );
              })()}
            </div>

            {selectedSale.note && (
              <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <span className="font-semibold">Notes:</span> {selectedSale.note}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalesPage;
